'use client';

import { faLock, faTrophy } from '@fortawesome/pro-regular-svg-icons';
import { faHandPointer, IconDefinition } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@pickleballinc/react-ui';
import clsx from 'clsx';
import moment from 'moment';
import { useEffect, useState } from 'react';

import { ClubMemberStatusTypes } from '@/modules/league/hooks/useClubMemberStatus';
import { RegistrationStatusTypes } from '@/modules/league/hooks/useRegistrationStatus';
import { SessionLeagueDetails } from '@/modules/league/new-components/types';

type RegistrationButtonProps = {
  isUserLoggedIn: boolean;
  status: RegistrationStatusTypes;
  session: SessionLeagueDetails;
  playerMembershipStatus: ClubMemberStatusTypes;
};

type RegistrationStatusTypesForIcons =
  | 'registration-completed'
  | 'registration-closed'
  | 'player-registered'
  | 'registration-open';

export const RegistrationButton = ({
  isUserLoggedIn,
  status,
  session,
  playerMembershipStatus,
}: RegistrationButtonProps) => {
  const [location, setLocation] = useState('');

  useEffect(() => {
    setLocation(window.location.href);
  }, []);

  const registrationCloseDate = moment
    .parseZone(session.Registration_DateClosed)
    .format('MMM DD, YYYY');
  const sessionNumber = session.SessionNumber;

  const pbracketsURL = `${process.env.NEXT_PUBLIC_PBRACKETS_URL}/redirect_r.aspx?mf=l&mid=${session.LeagueID}&cid=${session.ClubID}&sid=${session.LeagueSessionID}&bdo=0&rurl=${encodeURIComponent(
    location
  )}&rurl2=${encodeURIComponent(location)}`;

  const registrationURL = isUserLoggedIn
    ? pbracketsURL
    : `${process.env.NEXT_PUBLIC_SSO_ENDPOINT}/?flow=SelfService&continue=${encodeURIComponent(location)}`;

  const myScheduleURL = `${process.env.NEXT_PUBLIC_PL_URL}/leagues/my-schedule`;

  if (status === 'registration-for-members-only' && playerMembershipStatus === 'valid-membership') {
    status = 'registration-open';
  }

  const redirectURL = status === 'registration-open' ? registrationURL : myScheduleURL;

  const isDisabled =
    ['registration-closed', 'registration-completed', 'no-online-registration'].includes(status) ||
    (status === 'registration-for-members-only' && playerMembershipStatus === 'expired-membership');

  const isDisabledForMemberExpiring =
    status === 'registration-for-members-only' &&
    playerMembershipStatus === 'membership-will-expire';

  const isPrimary = status === 'player-registered' || status === 'registration-open';

  const buttonClasses = clsx('justify-center focus:!shadow-[0] w-full', {
    '!border-0 !bg-gray-200 pointer-events-none': isDisabled,
    '!border-0 !bg-gray-200 pointer-events-none flex flex-col': isDisabledForMemberExpiring,
    '!bg-[#E04F16] text-base hover:!bg-[#E04F16]/90 text-white': isPrimary,
  });

  const getStatusText = () => {
    switch (status) {
      case 'registration-closed':
        return 'Registration Closed';
      case 'registration-completed':
        return 'Completed';
      case 'player-registered':
        return 'My League Schedule';
      case 'registration-for-members-only':
        return 'Members Only';
      case 'no-online-registration':
        return 'No Online Registration';
      case 'registration-open':
      default:
        return `Click to Register Session: ${sessionNumber}`;
    }
  };

  const iconMap: Record<RegistrationStatusTypesForIcons, IconDefinition> = {
    'registration-closed': faLock,
    'registration-completed': faLock,
    'player-registered': faHandPointer,
    'registration-open': faTrophy,
  };

  const shouldShowIcon = Object.keys(iconMap).includes(status);

  const icon = shouldShowIcon ? iconMap[status as RegistrationStatusTypesForIcons] : undefined;

  return (
    <>
      {isDisabled || isDisabledForMemberExpiring ? (
        <Button
          variant='secondary'
          className={buttonClasses}
          prefixIcon={icon ? <FontAwesomeIcon icon={icon} /> : undefined}
        >
          {isDisabledForMemberExpiring ? (
            <>
              <span> {getStatusText()}</span>
              <span className='text-xs font-normal'>{`Your membership expires before ${registrationCloseDate}`}</span>
            </>
          ) : (
            getStatusText()
          )}
        </Button>
      ) : (
        <a href={redirectURL} aria-label={`Link to ${redirectURL}`}>
          <Button
            className={buttonClasses}
            prefixIcon={icon ? <FontAwesomeIcon icon={icon} /> : undefined}
          >
            {getStatusText()}
          </Button>
        </a>
      )}
    </>
  );
};
